import { Alert } from '@startuptools/ui/Alert';
import { Button } from '@startuptools/ui/Button';
import { Table } from '@startuptools/ui/Table';
import { Modal } from '@startuptools/ui/Modal';
import { ModalDialog } from '@startuptools/ui/ModalDialog';
import { Typography } from '@startuptools/ui/Typography';
import { Stack } from '@startuptools/ui/Stack';
import { GenericError } from '@startuptools/ui/Error';
import { StripeCurrencyFormat } from '@startuptools/ui/StripeCurrencyFormat';
import ErrorIcon from '@mui/icons-material/Error';
import {
  type GqlStripeInvoice,
  type GqlStripeUpcomingInvoice,
  useStripePayInvoiceMutation,
} from '../../graphql/react-operations';
import { isNil, toNumber } from 'lodash-es';
import { ApolloError } from '@apollo/client';
import { isPromise } from '../../helpers/is-promise';
import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react';

interface Props {
  invoice: GqlStripeInvoice | GqlStripeUpcomingInvoice;
  companyId: string;
  onCancel: () => void;
  onPaid: () => Promise<unknown> | void;
}

export function InvoiceDialog({ invoice, companyId, onCancel, onPaid }: Props) {
  const { i18n } = useLingui();
  // eslint-disable-next-line sut/loading-mutation
  const [payInvoice, payInvoiceState] = useStripePayInvoiceMutation({ fetchPolicy: 'no-cache' });
  const [loadingOnPaid, setLoadingOnPaid] = useState(false);
  const [showPmError, setShowPmError] = useState(false);
  const [showGenericError, setShowGenericError] = useState(false);
  if (isNil(invoice)) {
    return null;
  }

  const handleAccept = async () => {
    setShowPmError(false);
    setShowGenericError(false);
    if (!('id' in invoice)) {
      // when used with GqlStripeUpcomingInvoice we just return as if it was paid
      // the payment itself will be handled outside
      setLoadingOnPaid(true);
      const ret = onPaid();
      if (isPromise(ret)) {
        try {
          await ret.finally(() => {
            setLoadingOnPaid(false);
          });
        } catch (e) {
          if (e instanceof ApolloError) {
            if (e.graphQLErrors.some(m => m.message === 'NO_PAYMENT_METHOD')) {
              setShowPmError(true);
            }
          } else {
            setShowGenericError(true);
          }
        }
      }
      return;
    }
    try {
      await payInvoice({ variables: { companyId, invoiceId: invoice.id } });
      const ret = onPaid();
      if (isPromise(ret)) {
        setLoadingOnPaid(true);
      }
    } catch (e) {
      if (e instanceof ApolloError) {
        if (e.graphQLErrors.length === 0) {
          throw e;
        }
        // @ts-expect-error TS2532
        if (!isNil(e.graphQLErrors[0].extensions?.rawType)) {
          const ret = onPaid();
          if (isPromise(ret)) {
            setLoadingOnPaid(true);
            await ret.finally(() => setLoadingOnPaid(false));
          }
          return;
        }
      }
      throw e;
    }
  };

  const loading = payInvoiceState.loading || loadingOnPaid;

  return (
    <Modal open>
      <ModalDialog>
        <Typography level="h2">
          <Trans id="Invoice" />
        </Typography>
        <Table noWrap sx={{ tableLayout: 'auto' }}>
          <thead>
            <tr>
              <td>
                <Trans id="Description" />
              </td>
              <td align="right">
                <Trans id="Quantity" />
              </td>
              <td align="right">
                <Trans id="Price per item" />
              </td>
              <td align="right">
                <Trans id="Sum" />
              </td>
            </tr>
          </thead>
          <tbody>
            {invoice.lineItems.map(li => {
              return (
                <tr key={li.id}>
                  <td>{li.description}</td>
                  <td align="right">{li.quantity}</td>
                  <td align="right">
                    <StripeCurrencyFormat locale={i18n.locale} currency={li.currency}>
                      {toNumber(li.unit_amount_excluding_tax)}
                    </StripeCurrencyFormat>
                  </td>
                  <td align="right">
                    {!isNil(li.amount_excluding_tax) && (
                      <StripeCurrencyFormat locale={i18n.locale} currency={li.currency}>
                        {li.amount_excluding_tax}
                      </StripeCurrencyFormat>
                    )}
                  </td>
                </tr>
              );
            })}
            <tr>
              <td rowSpan={3}></td>
              <td colSpan={2}>
                <Trans id="Subtotal" />
              </td>
              <td align="right">
                <StripeCurrencyFormat locale={i18n.locale} currency={invoice.currency}>
                  {invoice.subtotal}
                </StripeCurrencyFormat>
              </td>
            </tr>
            <tr>
              <td colSpan={2}>MOMS (25%)</td>
              <td align="right">
                {!isNil(invoice.tax) && (
                  <StripeCurrencyFormat locale={i18n.locale} currency={invoice.currency}>
                    {invoice.tax}
                  </StripeCurrencyFormat>
                )}
              </td>
            </tr>
            <tr>
              <td colSpan={2}>
                <b>
                  <Trans id="To pay now" />
                </b>
              </td>
              <td align="right">
                <b>
                  <StripeCurrencyFormat locale={i18n.locale} currency={invoice.currency}>
                    {invoice.amount_remaining}
                  </StripeCurrencyFormat>
                </b>
              </td>
            </tr>
          </tbody>
        </Table>
        {showPmError && (
          <Alert variant="outlined" color="danger" startDecorator={<ErrorIcon />}>
            <Trans id="Could not find a default payment method, please set any existing card as default or add a new card and try again" />
          </Alert>
        )}
        {showGenericError && <GenericError />}
        <Stack direction="row" gap={1} justifyContent="right">
          <Button disabled={loading} onClick={() => onCancel()} variant="plain">
            <Trans id="Cancel" />
          </Button>
          <Button onClick={() => handleAccept()} loading={loading}>
            <Trans id="Pay" />
          </Button>
        </Stack>
      </ModalDialog>
    </Modal>
  );
}
